import { useEffect, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";

import type { ContraTransferType } from "~/v2/api/rest/acats";
import { FAQ } from "~/v2/components/common/FAQ";
import { Path } from "~/v2/path";
import { Layout } from "../../common/Layout";
import type { AcatsLocationState } from "../../common/types";
import { getContraBrokerDisplayName } from "../../common/util";
import { BackButton } from "../BackButton";
import { AssetSelectionForm } from "./AssetSelectionForm";
import { ASSETS_HOLDING_FAQ } from "./FAQ";
import { FullTransferForm } from "./FullTransferForm";

// Ask if user is eligible for full transfer
// If yes, ask if they want to do a full transfer or transfer selected assets
// If no, push to partial transfer page

export type Selection = "Yes" | "No" | "I'm not sure" | null;
type State = {
  assetSelection: Selection;
  isAskingFullTransfer: boolean;
  fullTransferSelection: ContraTransferType | null;
};

const AssetsHoldingPage = () => {
  const history = useHistory();
  // Get data from search params and tax id from state
  const { state: locationState } = useLocation<AcatsLocationState>();

  const [state, setState] = useState<State>({
    assetSelection: null,
    isAskingFullTransfer: false,
    fullTransferSelection: null,
  });

  const onBackClicked = () =>
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER,
      state: undefined,
    });

  const onPreviousClicked = () => {
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ACCOUNT_INFO,
      state: locationState,
    });
  };

  const onFullTransferFormPreviousClicked = () => {
    setState((prevState) => ({
      ...prevState,
      isAskingFullTransfer: false,
    }));
  };

  const pushToNextPage = (type: ContraTransferType) => {
    history.replace({
      pathname:
        type === "PARTIAL"
          ? Path.ROUTE_PORTFOLIO_TRANSFER_PARTIAL_TRANSFER
          : Path.ROUTE_PORTFOLIO_TRANSFER_REVIEW,
      state: {
        ...locationState,
        transferType: type,
        // edge case, remove potential equities or cash balance for full transfers
        equities: type === "FULL" ? undefined : locationState?.equities,
        cashBalance: type === "FULL" ? undefined : locationState?.cashBalance,
      },
    });
  };

  const onTransferTypeSelected = (type: ContraTransferType) => {
    setState((prevState) => ({
      ...prevState,
      fullTransferSelection: type,
    }));
    pushToNextPage(type);
  };

  const onAssetSelectionClicked = (selection: Selection) => {
    const type =
      selection === "Yes" || selection === "I'm not sure" ? "PARTIAL" : "FULL";

    // Eligible for full transfer, ask full transfer question
    if (type === "FULL") {
      setState((prevState) => ({
        ...prevState,
        assetSelection: selection,
        isAskingFullTransfer: true,
      }));
      return;
    }

    pushToNextPage(type);
  };

  useEffect(() => {
    // if account number is not in params, go back to account info page
    if (!locationState?.accountNumber || !locationState?.taxId) {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ACCOUNT_INFO,
        state: locationState,
      });
      return;
    }
    // if broker name or dtc number is not in params, go back to originating broker page
    if (!locationState?.brokerName || !locationState?.brokerNumber) {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ORIGINATING_BROKER,
        state: locationState,
      });
    }
  }, [locationState, history]);

  const brokerName = getContraBrokerDisplayName(
    locationState?.brokerName ?? "Broker",
  );

  return (
    <div>
      <BackButton
        onClick={onBackClicked}
        label="Back to Transfer Funds"
        className="mb-4"
      />
      <Layout
        title={
          state.isAskingFullTransfer
            ? "You are eligible for a full transfer"
            : `Does your ${brokerName} account hold assets other than cash, stocks, or ETFs?`
        }
        faq={<FAQ faq={ASSETS_HOLDING_FAQ} />}
        className="mt-2 pt-0"
      >
        {state.isAskingFullTransfer ? (
          <FullTransferForm
            onTransferTypeSelected={onTransferTypeSelected}
            onPreviousClicked={onFullTransferFormPreviousClicked}
          />
        ) : (
          <AssetSelectionForm
            onPreviousClicked={onPreviousClicked}
            onSelectionClicked={onAssetSelectionClicked}
          />
        )}
      </Layout>
    </div>
  );
};

export default AssetsHoldingPage;
